import { nectarApi } from '@/features/nectar/api/client'
import { parseMoney } from '@/utils'
import { type InfiniteData, type UseInfiniteQueryResult, useInfiniteQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import type { EnrollmentSubmission, EnrollmentSubmissionsEnvelope } from './models'

type EnrollmentSubmissionWire = Omit<EnrollmentSubmission, 'amountUsd'> & {
  amountUsd: string | null
}

type EnrollmentSubmissionsEnvelopeWire = {
  data: EnrollmentSubmissionWire[]
  meta: EnrollmentSubmissionsEnvelope['meta']
}

type UseFetchEnrollmentSubmissionsArgs = {
  id: string
  limit: number
  enabled: boolean
}

export const useFetchEnrollmentSubmissions = ({
  id,
  limit,
  enabled
}: UseFetchEnrollmentSubmissionsArgs): UseInfiniteQueryResult<InfiniteData<EnrollmentSubmissionsEnvelope>> => {
  return useInfiniteQuery({
    queryKey: ['enrollment-submissions', { id, limit }],
    queryFn: async ({ pageParam }): Promise<EnrollmentSubmissionsEnvelope> => {
      try {
        const wire = await nectarApi.get<EnrollmentSubmissionsEnvelopeWire>(`/v1/enrollments/${id}/submissions`, {
          page: pageParam,
          limit
        })
        return {
          data: wire.data.map((row) => ({
            ...row,
            amountUsd: row.amountUsd === null ? null : parseMoney(row.amountUsd)
          })),
          meta: wire.meta
        }
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          return { data: [], meta: { total: 0, page: pageParam, limit } }
        }
        throw err
      }
    },
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.total > last.meta.page * last.meta.limit ? last.meta.page + 1 : undefined),
    staleTime: 30_000,
    enabled
  })
}
